// File: client/src/writer/contexts/AppContext.tsx
import React, { createContext, useContext, ReactNode } from 'react';
import { Editor } from '@tiptap/react';
import { ProjectState, Chapter, LoadingStates, MainView, ImageGenerationContextType, ImageGenerationPromptData, OptimizedDataLoading } from '../types';
import { ChapterManagementLogic } from '../hooks/useChapterManagement';
import { ChapterAIActions } from '../hooks/useChapterAIActions';
import { FileAndCoverActions } from '../hooks/useFileAndCoverActions';
import { ModalControls } from '../hooks/useModalStates';
import { ConfirmationLogic } from '../hooks/useConfirmation';
import { ChapterState } from '../hooks/useChapterState';
import { ProjectSetupLogic } from '../hooks/useProjectSetupLogic';
import { CoreState } from '../hooks/useCoreState';

export interface AppContextType extends Omit<CoreState, 'setProjectState' | 'error'> {
    setProjectState: React.Dispatch<React.SetStateAction<ProjectState | null>>; // Override with correct type
    editor: Editor | null; // Allow editor to be null during initialization
    chapterManagement: ChapterManagementLogic;
    chapterAIActions: ChapterAIActions;
    fileAndCoverActions: FileAndCoverActions;
    modalStates: ModalControls;
    confirmationControls: ConfirmationLogic;
    chapterState: ChapterState;
    projectSetupLogic: ProjectSetupLogic;
    setMainView: (view: MainView) => void;
    hasInitialBlueprint: boolean; // Added to satisfy WriteStudio.tsx's requirement
    optimizedData: OptimizedDataLoading; // Add optimized data for blueprint state management
    openImageGenerationModalFromContext: (context: ImageGenerationContextType, initialPrompt?: string | ImageGenerationPromptData) => void;
    handleExportChapterContentPreferenceChange: (value: 'preview' | 'content') => void;
    handleGenerateIndividualBookStructureTextElement: (elementType: any, isFrontMatter: boolean) => void;
    // Error handling for blueprint generation
    error?: string | null;
    failedModel?: string | null;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ value: AppContextType; children: ReactNode }> = ({ value, children }) => {
    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};